import { useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Autocomplete, Box, TextField } from "@mui/material"
import { PopupForm, DividerAdd } from "@tracabois/components"
import { RemovalZone, RemovalZoneCreateCommand } from "../../../../removalZone/model/removalZone"
import { useRemovalZonePageQuery, useRemovalZoneCreateMutation } from "../../../../removalZone/api/RemovalZoneApi"
import { allPage } from "@tracabois/utils"
import { RealizationId } from "../../../model/realizationModel"
import { FormComposable, FormComposableField } from "@tracabois/components"
import { useFormComposable } from "@komune-io/g2-composable"
import { FormAction } from "@komune-io/g2-forms"
import { Stack } from "@mui/material"

interface OriginesAddFormPopupProps {
  open: boolean
  onClose: () => void
  realizationId: RealizationId
  existingZoneIds: string[]
  onAddRemovalZone: (removalZoneId: string) => Promise<void>
}

const useLabels = () => {
  const { t } = useTranslation()
  return useMemo(() => ({
    title: t("realization.origines.add", "Ajouter des origines"),
    search: t("realization.origines.search", "Rechercher une zone de prélèvement"),
    create: t("realization.origines.create", "Créer une zone"),
    select: t("realization.origines.select", "Sélectionner une zone"),
  }), [t])
}

export const OriginesAddFormPopup = (props: OriginesAddFormPopupProps) => {
  const { open, onClose, realizationId, existingZoneIds, onAddRemovalZone } = props
  const labels = useLabels()
  const [showCreation, setShowCreation] = useState<boolean>(false)

  const { data: allZonesData } = useRemovalZonePageQuery({
    pagination: allPage,
    search: undefined,
    filters: {}
  })
  const allZones: RemovalZone[] = allZonesData?.items ?? []

  const availableZones = useMemo(() =>
    allZones.filter(z => !existingZoneIds.includes(z.id)),
    [allZones, existingZoneIds]
  )

  const handleSelect = useCallback((_: any, value: RemovalZone | null) => {
    if (value) {
      onAddRemovalZone(value.id)
    }
  }, [onAddRemovalZone])

  return (
    <PopupForm open={open} onClose={onClose} title={labels.title}>
      <Box minWidth={500}>
        {!showCreation && (<>
          <DividerAdd loading={false} readOnly={false} onClick={() => setShowCreation(true)} icon={<>{labels.create}</>} />
          <Autocomplete
            options={availableZones}
            getOptionLabel={(option) => option.name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={handleSelect}
            renderInput={(params) => (
              <TextField
                {...params}
                label={labels.search}
                size="small"
                autoFocus
              />
            )}
          />
        </>)}

        {showCreation && (<>
          <DividerAdd loading={false} readOnly={false} onClick={() => setShowCreation(false)} icon={<>{labels.select}</>} />
          <OriginesCreateForm
            onCancel={onClose}
            onCreated={onAddRemovalZone}
          />
        </>)}
      </Box>
    </PopupForm>
  )
}

interface OriginesCreateFormProps {
  onCancel: () => void
  onCreated: (removalZoneId: string) => Promise<void>
}

const OriginesCreateForm = (props: OriginesCreateFormProps) => {
  const { onCancel, onCreated } = props
  const [createRemovalZone] = useRemovalZoneCreateMutation()
  const [feedback, setFeedback] = useState<boolean | undefined>(undefined)

  const onSubmitMemoized = useCallback(
    async (values: { name: string; department?: string; city?: string }) => {
      try {
        const cmd: Partial<RemovalZoneCreateCommand> = {
          name: values.name,
        }
        if (values.department) {
          // @ts-ignore - department is an object with code and name
          cmd.department = { code: values.department, name: values.department }
        }
        if (values.city) {
          // @ts-ignore - city is an object with code and name
          cmd.city = { code: values.city, name: values.city }
        }
        const result = await createRemovalZone(cmd as RemovalZoneCreateCommand).unwrap()
        await onCreated(result.id)
        setFeedback(true)
        return true
      } catch {
        setFeedback(false)
        return false
      }
    },
    [createRemovalZone, onCreated]
  )

  const formState = useFormComposable({
    onSubmit: onSubmitMemoized
  })

  const fields = useMemo(
    (): FormComposableField[] => [
      {
        key: "name",
        name: "name",
        type: "textField",
        label: "Nom",
        params: {
          placeholder: "Nom de la zone"
        },
        validator: (value?: string) => {
          const trimmed = (value ?? "").trim()
          if (!trimmed) return "Vous devez renseigner le nom" as string
          return undefined
        }
      },
      {
        key: "department",
        name: "department",
        type: "textField",
        label: "Département",
        params: {
          placeholder: "Département"
        }
      },
      {
        key: "city",
        name: "city",
        type: "textField",
        label: "Commune",
        params: {
          placeholder: "Commune"
        }
      },
    ],
    []
  )

  const actions: FormAction[] = [{
    key: "cancel",
    label: "Annuler",
    onClick: onCancel,
    variant: "text"
  }, {
    key: "create",
    label: "Créer",
    success: (feedback !== undefined && feedback),
    fail: (feedback !== undefined && !feedback),
    onClick: formState.submitForm
  }]

  return (
    <Stack alignItems="center" width="100%" spacing={2}>
      <Stack direction="row" flexWrap="wrap" justifyContent="space-between" width="100%">
        <FormComposable
          fields={fields}
          formState={formState}
          actions={actions}
          style={{ width: "100%" }}
          actionsProps={{
            direction: "row",
            justifyContent: "flex-end",
            width: "100%",
            spacing: 2
          }}
        />
      </Stack>
    </Stack>
  )
}
